import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchConnectedEmails,
  EmailItem,
  EmailDetail,
} from '@core/api/integrations/integrationsApi';
import {
  fetchConnectedEmails,
  fetchMoreConnectedEmails,
  fetchConnectedMailFolderCounts,
  fetchConnectedEmailDetail,
  clearConnectedEmailDetail,
  markConnectedEmailRead,
  resetConnectedIntegrationState,
  isConnectionNotFoundError,
} from '@app/actions/integrations/emails';
import {
  getOutlookEmails,
  getOutlookEmailDetail,
} from '@core/selectors/integrations';
import type {
  OutlookMessage,
  OutlookThread,
  OutlookThreadMessage,
} from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROVIDER = 'microsoft' as const;
const INBOX_FOLDER = 'inbox';
const INBOX_TOP = 25;

/** Fixed avatar background — same in light and dark (MUI default-avatar style). */
const AVATAR_COLOR = '#5D536C';

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

export const getSenderInitials = (name?: string): string => {
  if (!name) return '?';

  let cleanName = name.trim();
  if (cleanName.includes('@')) {
    cleanName = cleanName.split('@')[0].replace(/[._-]/g, ' ');
  }

  // Remove commas, quotes, parentheses etc. (e.g. "Doe, John" -> "Doe John")
  cleanName = cleanName.replace(/[,()[\]{}"']/g, ' ');

  const parts = cleanName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  // If 2 or more words, take first character of first 2 words
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

const formatAttachmentSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

type DateLabels = {
  receivedLabel: string;
  receivedTime?: string;
  receivedDateLabel?: string;
};

const formatReceivedDate = (dateStr?: string): DateLabels => {
  if (!dateStr) return { receivedLabel: '' };

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return { receivedLabel: '' };

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const msgDayStart = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (msgDayStart.getTime() === todayStart.getTime()) {
    return { receivedLabel: 'Today', receivedTime: timeStr };
  }
  if (msgDayStart.getTime() === yesterdayStart.getTime()) {
    return { receivedLabel: 'Yesterday', receivedTime: timeStr };
  }

  const dateLabel = date
    .toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    })
    .replace(',', '');
  return { receivedLabel: dateLabel, receivedDateLabel: dateLabel };
};

const emailItemToOutlookMessage = (item: EmailItem): OutlookMessage => {
  const senderName = item.from?.name || item.from?.address || 'Unknown Sender';
  const { receivedLabel, receivedTime, receivedDateLabel } = formatReceivedDate(
    item.receivedDateTime
  );

  return {
    id: item.id,
    threadId: item.conversationId ?? item.id,
    senderName,
    senderInitials: getSenderInitials(senderName),
    senderColor: AVATAR_COLOR,
    subject: item.subject || '(No Subject)',
    preview: item.bodyPreview,
    receivedLabel,
    receivedTime,
    receivedDateLabel,
    isUnread: !item.isRead,
    isImportant: item.importance?.toLowerCase() === 'high',
    hasAttachment: item.hasAttachments ?? false,
    webLink: item.webLink,
  };
};

const emailDetailToThreadMessage = (
  detail: EmailDetail
): OutlookThreadMessage => {
  const senderName =
    detail.from?.name ||
    detail.sender?.name ||
    detail.from?.address ||
    detail.sender?.address ||
    'Unknown Sender';

  const recipientNames = (detail.toRecipients ?? [])
    .map((r) => r.name || r.address || '')
    .filter(Boolean)
    .join(', ');

  const sentDateStr = (detail as any).sentDateTime ?? detail.receivedDateTime;
  const { receivedLabel, receivedTime } = formatReceivedDate(sentDateStr);
  const sentLabel =
    receivedLabel === 'Today' || receivedLabel === 'Yesterday'
      ? `${receivedLabel} ${receivedTime ?? ''}`.trim()
      : receivedLabel;

  // Preserve the raw body — HTML is rendered in a sandboxed iframe
  const rawBody = detail.body?.content ?? detail.bodyPreview ?? '';
  const bodyIsHtml = (detail.body?.contentType ?? '').toLowerCase() === 'html';

  const attachments = (detail.attachments ?? [])
    .filter((a) => !a.isInline)
    .map((a) => ({
      id: a.id,
      name: a.name,
      size: formatAttachmentSize(a.size),
    }));

  return {
    id: detail.id,
    conversationId: detail.conversationId,
    senderName,
    senderEmail: detail.sender?.address || detail.from?.address,
    senderInitials: getSenderInitials(senderName),
    senderColor: AVATAR_COLOR,
    recipients: `To: ${recipientNames || 'Unknown'}`,
    sentLabel,
    body: rawBody,
    bodyIsHtml,
    isImportant: (detail as any).importance?.toLowerCase() === 'high',
    webLink: detail.webLink,
    attachments: attachments.length ? attachments : undefined,
  };
};

/**
 * Maps an EmailDetail into an OutlookThread.
 *
 * If the API returned a `thread` array it already contains all messages
 * (including the clicked email itself) — use that array directly.
 * Otherwise fall back to a single-message thread containing just `detail`.
 *
 * Messages are stored oldest-first; the dialog reverses them to show newest first.
 */
export const emailDetailToOutlookThread = (
  detail: EmailDetail
): OutlookThread => {
  const threadMessages = (detail.thread ?? []) as EmailDetail[];

  const allMessages: EmailDetail[] =
    threadMessages.length > 0
      ? // API already includes the clicked email in `thread`; reverse to get oldest-first
        [...threadMessages].reverse()
      : // No thread context — just show the single email
        [detail];

  return {
    id: detail.conversationId ?? detail.id,
    subject: detail.subject,
    messages: allMessages.map(emailDetailToThreadMessage),
  };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type OutlookEmailsError = 'load-error' | null;

const useOutlookEmails = (isConnected: boolean) => {
  const dispatch = useDispatch<any>();
  const emailsState = useSelector(getOutlookEmails);
  const detailState = useSelector(getOutlookEmailDetail);

  const emails = useMemo(
    () => emailsState.rawItems.map(emailItemToOutlookMessage),
    [emailsState.rawItems]
  );

  const selectedThread = useMemo(
    () =>
      detailState.rawDetail
        ? emailDetailToOutlookThread(detailState.rawDetail)
        : null,
    [detailState.rawDetail]
  );

  // ----------------------------------
  // Inbox list (initial / refresh)
  // ----------------------------------

  const fetchEmails = useCallback(
    (force = false) => {
      if (!isConnected) return;
      dispatch(
        fetchConnectedEmails(PROVIDER, {
          folder: INBOX_FOLDER,
          top: INBOX_TOP,
          force,
        })
      );
    },
    [dispatch, isConnected]
  );

  // ----------------------------------
  // Inbox list pagination (next page via @odata.nextLink)
  // ----------------------------------

  const fetchNextPage = useCallback(() => {
    if (!isConnected) return;
    dispatch(fetchMoreConnectedEmails(PROVIDER));
  }, [dispatch, isConnected]);

  // ----------------------------------
  // Unread badge count
  // ----------------------------------

  const fetchFolderCounts = useCallback(
    (force = false) => {
      if (!isConnected) return;
      dispatch(
        fetchConnectedMailFolderCounts(PROVIDER, INBOX_FOLDER, { force })
      );
    },
    [dispatch, isConnected]
  );

  useEffect(() => {
    if (isConnected) {
      fetchEmails();
      fetchFolderCounts();
    }
  }, [isConnected, fetchEmails, fetchFolderCounts]);

  // ----------------------------------
  // Email detail / thread dialog
  // ----------------------------------

  const openEmailDetail = useCallback(
    (emailId: string) => {
      dispatch(fetchConnectedEmailDetail(PROVIDER, emailId));
    },
    [dispatch]
  );

  const closeThread = useCallback(() => {
    dispatch(clearConnectedEmailDetail());
  }, [dispatch]);

  // ----------------------------------
  // Mark as read
  // ----------------------------------

  const markEmailRead = useCallback(
    (emailId: string) => {
      dispatch(markConnectedEmailRead(emailId));
    },
    [dispatch]
  );

  // ----------------------------------
  // Search (called with debounce from AllEmailsDialog)
  // Supports initial query or subsequent page with nextLink
  // ----------------------------------

  const searchEmails = useCallback(
    async (
      query?: string,
      searchNextLink?: string
    ): Promise<{ emails: OutlookMessage[]; nextLink?: string }> => {
      try {
        const { emails: raw, nextLink: resNextLink } =
          await searchConnectedEmails(PROVIDER, query, {
            folder: INBOX_FOLDER,
            nextLink: searchNextLink,
          });
        return {
          emails: raw.map(emailItemToOutlookMessage),
          nextLink: resNextLink,
        };
      } catch (err) {
        if (isConnectionNotFoundError(err)) {
          dispatch(resetConnectedIntegrationState());
        }
        throw err;
      }
    },
    [dispatch]
  );

  const refresh = useCallback(() => {
    fetchEmails(true);
    fetchFolderCounts(true);
  }, [fetchEmails, fetchFolderCounts]);

  return {
    emails,
    nextLink: emailsState.nextLink,
    hasMoreEmails: emailsState.hasMoreEmails,
    isLoadingEmails: emailsState.isLoadingEmails,
    isLoadingMoreEmails: emailsState.isLoadingMoreEmails,
    fetchNextPage,
    emailsError: emailsState.emailsError,
    selectedThread,
    isLoadingDetail: detailState.isLoadingDetail,
    unreadCount: emailsState.unreadCount,
    refresh,
    openEmailDetail,
    closeThread,
    markEmailRead,
    searchEmails,
  };
};

export default useOutlookEmails;
